import {
  Activity, AudioLines, BookUser, Building2, CircleUser, Globe, GraduationCap, Hash, History, Inbox,
  Landmark, ListChecks, Megaphone, MessagesSquare, Milestone, PhoneCall, PhoneIncoming, PhoneOff, PhoneOutgoing,
  PlugZap, Radio, Repeat, Server, Settings, Target, Users, UsersRound, Voicemail, Waypoints, Webhook,
  type LucideIcon,
} from "lucide-react";
import { atLeast, type Role } from "@/lib/types";

/**
 * The console's route map, in one place.
 *
 * The sidebar, the Settings page, the post-login redirect and the per-route guard all read from
 * here. When they were three separate lists, signing in could land somebody on a page their role
 * cannot open — the guard said no, the redirect did not know that, and the nav simply omitted the
 * link.
 *
 * A page goes in one of two places. The sidebar (`NAV_SECTIONS`) is for the pages somebody works
 * in most days: the phone, contacts, campaigns, the floor, the reports. Everything that is set up
 * once and changed now and then — a catalog, a library, the telephony, an integration — goes in
 * `SETTINGS_SECTIONS`, one click down behind Settings at the foot of the sidebar. The sidebar held
 * both until it ran to 27 links for a superadmin; asking "does somebody open this most days?" of
 * each new page is what keeps it short.
 */

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Hidden from the nav and refused by the guard below this role. */
  minRole: Role;
  /** A short tag after the label, e.g. to mark a screen that is still a placeholder. */
  badge?: string;
  /** One line under the label on the Settings page's card: what the page is for. */
  description?: string;
}

export interface NavSection {
  heading: string;
  items: NavItem[];
}

export const NAV_SECTIONS: NavSection[] = [
  {
    heading: "Workspace",
    items: [
      { to: "/phone", label: "Phone", icon: PhoneCall, minRole: "rep" },
      // Everybody's own calls, including a rep's — which is the point. `/calls` below is the
      // floor's report and stays at supervisor; these are two screens because they answer two
      // questions ("how did my day go" vs "what is the floor doing"), and because a rep must
      // never be one cleared filter away from the second.
      { to: "/history", label: "Call history", icon: History, minRole: "rep" },
    ],
  },
  {
    heading: "CRM",
    items: [
      // Every signed-in person, as `GET /leads` is: a rep sees the contacts of the campaigns whose
      // lines they staff and the ones they have worked, a supervisor the tenant's (ADR-044).
      { to: "/contacts", label: "Contacts", icon: BookUser, minRole: "rep" },
      // The client's sheet as a table (ADR-045): supervisors read it, admins edit it. The firms are
      // records the floor looks things up in, like the contacts, not settings.
      { to: "/firms", label: "Firms", icon: Landmark, minRole: "supervisor" },
    ],
  },
  {
    heading: "People",
    items: [{ to: "/users", label: "Users", icon: Users, minRole: "supervisor" }],
  },
  {
    heading: "Contact Center",
    items: [
      { to: "/campaigns", label: "Campaigns", icon: Megaphone, minRole: "supervisor" },
      { to: "/floor", label: "Live floor", icon: Activity, minRole: "supervisor" },
    ],
  },
  {
    heading: "Analytics",
    items: [
      // The three reports (ADR-074), each its own screen: `/reports/*` is supervisor-and-above, as
      // `/acd/stats` is.
      { to: "/reports/lines", label: "Line report", icon: PhoneIncoming, minRole: "supervisor" },
      { to: "/reports/campaigns", label: "Campaign report", icon: Target, minRole: "supervisor" },
      { to: "/reports/reps", label: "Rep report", icon: UsersRound, minRole: "supervisor" },
      { to: "/calls", label: "Calls", icon: Waypoints, minRole: "supervisor" },
    ],
  },
];

/** The Settings page, and the entry at the foot of the sidebar that opens it. */
export const SETTINGS_ITEM: NavItem = {
  to: "/settings",
  label: "Settings",
  icon: Settings,
  minRole: "rep",
};

/**
 * The pages behind Settings. The headings are the ones these pages had in the sidebar, so a page
 * is found under the name people already looked for it by.
 */
export const SETTINGS_SECTIONS: NavSection[] = [
  {
    heading: "Contact Center",
    items: [
      // The follow-up policies a campaign runs (ADR-066): supervisors read them, admins edit.
      {
        to: "/cadences", label: "Cadences", icon: Repeat, minRole: "supervisor",
        description: "When a lead is called next, and how often. A campaign runs one from its Dialing tab.",
      },
      // `GET /skills` is supervisor-and-above: a supervisor reads the catalog, an admin edits it.
      {
        to: "/skills", label: "Skills", icon: GraduationCap, minRole: "supervisor",
        description: "What a line, a number or a handoff needs in the rep it rings.",
      },
      // A rep reads `GET /dispositions` too — their buttons after a call are this list — but on the
      // phone. The catalog is a supervisor's page to read and an admin's to edit.
      {
        to: "/dispositions", label: "Dispositions", icon: ListChecks, minRole: "supervisor",
        description: "The outcomes a rep picks after a call, and what each one does to the lead.",
      },
      // Supervisors read the store, as they read the campaign settings that pick from it; the API
      // lets only admins upload, rename and delete, and the page follows it.
      {
        to: "/audio", label: "Audio", icon: AudioLines, minRole: "supervisor",
        description: "The sound files your lines, campaigns and voicemail messages play.",
      },
      // Everyone signed in reads `GET /voicemail-messages` — a rep picks one on a dialer call — but on
      // the phone. The library names each recording from `GET /audio/library`, which is
      // supervisor-and-above, so it is a supervisor's page to read and an admin's to edit.
      {
        to: "/voicemail-messages", label: "Voicemail messages", icon: Voicemail, minRole: "supervisor",
        description: "The messages a rep can leave when a dialer call reaches voicemail.",
      },
      // Everyone signed in reads `GET /speed-dials` — a rep picks one from Add participant on a
      // call — but on the phone. The list is a supervisor's page to read and an admin's to edit.
      {
        to: "/speed-dials", label: "Speed dials", icon: PhoneOutgoing, minRole: "supervisor",
        description: "Outside numbers a rep can bring into a call by name.",
      },
      // Everyone signed in reads `GET /blocked-callers` and blocks from the phone, for up to a day
      // (ADR-159). The list, blocking for good and unblocking early are a supervisor's.
      {
        to: "/blocked-callers", label: "Blocked callers", icon: PhoneOff, minRole: "supervisor",
        description: "Callers turned away before any phone rings, for a while or for good.",
      },
    ],
  },
  {
    heading: "CRM",
    items: [
      // The lifecycle a contact moves through (ADR-077). Everyone signed in reads `GET /stages` —
      // every stage label does — but the catalog is a supervisor's page to read and an admin's to edit.
      {
        to: "/stages", label: "Stages", icon: Milestone, minRole: "supervisor",
        description: "The lifecycle every contact moves through, and what each stage means for calling.",
      },
    ],
  },
  {
    heading: "Telephony",
    items: [
      {
        to: "/numbers", label: "Numbers", icon: Hash, minRole: "supervisor",
        description: "The phone numbers you hold, and where a call to each one goes.",
      },
      {
        to: "/endpoints", label: "Endpoints", icon: Server, minRole: "supervisor",
        description: "The phones and devices registered on the switch.",
      },
      {
        to: "/domains", label: "Domains", icon: Globe, minRole: "supervisor",
        description: "The SIP realms phones register in, with their outbound routing and transcription.",
      },
      // Trunks carry the carrier username and are what every domain's calls ride, so the
      // API refuses anyone below admin — the nav must agree or the link 403s on click.
      {
        to: "/gateways", label: "Gateways", icon: Radio, minRole: "admin",
        description: "The SIP trunks calls leave on.",
      },
    ],
  },
  {
    heading: "Integrations",
    items: [
      // Admins only, as core-api's `/webhooks/*` routes are: an endpoint sends the tenant's events,
      // with its leads' names and numbers, to a URL outside the platform (ADR-022).
      {
        to: "/slack", label: "Slack", icon: MessagesSquare, minRole: "admin",
        description: "Connect a Slack workspace, then pick its channels and events in Webhooks.",
      },
      {
        to: "/webhooks", label: "Webhooks", icon: Webhook, minRole: "admin",
        description: "Send the events you choose to Slack, Zapier or your CRM.",
      },
      // Admins only too: a trigger's URL can add leads to a dial list, hold them, and mark them
      // do-not-call (ADR-021).
      {
        to: "/triggers", label: "Triggers", icon: PlugZap, minRole: "admin",
        description: "Let another system act on your leads through a URL of its own.",
      },
    ],
  },
  {
    heading: "Platform",
    items: [
      {
        to: "/inventory", label: "Number inventory", icon: Inbox, minRole: "superadmin",
        description: "Every number the platform holds, who has it, and the requests waiting on you.",
      },
      {
        to: "/tenants", label: "Tenants", icon: Building2, minRole: "superadmin",
        description: "The organisations on the platform. Every user but a superadmin is in one.",
      },
    ],
  },
];

/**
 * The signed-in person's own page. It is on the Settings page, last, but it is every role's, so
 * it is not on its own a reason to show Settings: a rep reaches it from their menu.
 */
export const ACCOUNT_ITEM: NavItem = {
  to: "/settings/account",
  label: "Account",
  icon: CircleUser,
  minRole: "rep",
  description: "Your details, your password and your phone.",
};

const ALL_ITEMS = [...NAV_SECTIONS, ...SETTINGS_SECTIONS].flatMap((s) => s.items).concat(ACCOUNT_ITEM);

function filterSections(sections: NavSection[], role: Role | undefined): NavSection[] {
  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => atLeast(role, item.minRole)),
    }))
    .filter((section) => section.items.length > 0);
}

/** This path, or a page under it — what `NavLink` matches on without `end`. */
function under(path: string, to: string): boolean {
  return path === to || path.startsWith(`${to}/`);
}

/** The sidebar's sections, as far as this role can see them. */
export function visibleSections(role: Role | undefined): NavSection[] {
  return filterSections(NAV_SECTIONS, role);
}

/** The Settings page's sections, as far as this role can see them — without the account. */
export function visibleSettingsSections(role: Role | undefined): NavSection[] {
  return filterSections(SETTINGS_SECTIONS, role);
}

/** Whether this role has anything behind Settings but its own account, and so sees the entry. */
export function hasSettings(role: Role | undefined): boolean {
  return visibleSettingsSections(role).length > 0;
}

/**
 * Whether a path is somewhere behind Settings: the page itself, the account, one of its pages, or
 * a record on one (`/webhooks/3`). The sidebar marks Settings as the place you are on all of them,
 * since none of them has a link of its own there.
 */
export function isSettingsPath(path: string): boolean {
  if (under(path, SETTINGS_ITEM.to)) return true;
  return SETTINGS_SECTIONS.some((s) => s.items.some((item) => under(path, item.to)));
}

/**
 * The "← Settings" link a page reached from the Settings page shows over its title, or null.
 * Only on the pages the cards open: a record under one (`/webhooks/3`) already links back to its
 * list. Not for a role with no Settings page to go back to — a rep on their account.
 */
export function settingsBackLink(
  role: Role | undefined,
  path: string,
): { to: string; label: string } | null {
  if (!hasSettings(role)) return null;
  const onSettingsPage =
    path === ACCOUNT_ITEM.to || SETTINGS_SECTIONS.some((s) => s.items.some((item) => item.to === path));
  return onSettingsPage ? { to: SETTINGS_ITEM.to, label: SETTINGS_ITEM.label } : null;
}

/** Where this role belongs when it has not asked for anywhere in particular. */
export function landingPath(role: Role | undefined): string {
  return atLeast(role, "supervisor") ? "/users" : "/phone";
}

/**
 * Whether a role may open a path. Unknown paths (`/settings`, and anything not in the nav)
 * return true — they are open to every signed-in user, and defaulting to false would lock
 * people out of pages as they are added.
 */
export function canAccess(role: Role | undefined, path: string): boolean {
  const item = ALL_ITEMS.find((i) => under(path, i.to));
  return item ? atLeast(role, item.minRole) : true;
}

/**
 * Where to send someone after signing in. Honours the page they were trying to reach, unless
 * their role cannot open it — a rep bounced off `/users` should not be bounced straight back
 * onto it after logging in, which is the loop this prevents.
 */
export function postLoginPath(role: Role | undefined, intended: string | undefined): string {
  if (!intended || intended === "/login") return landingPath(role);
  const path = intended.split("?")[0] ?? intended;
  return canAccess(role, path) ? intended : landingPath(role);
}
